import { computed, inject, Injectable, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { map, of } from 'rxjs';

import { PasswordResetHttpService } from './password-reset-http.service';

export interface PasswordResetRequestResourceValue {
  email: string;
  courtId: number;
  requestId: number;
}

@Injectable({ providedIn: 'root' })
export class PasswordResetRequestResourceService {
  private readonly _http = inject(PasswordResetHttpService);

  private readonly _requestId = signal(0);
  private readonly _request = signal<{
    email: string;
    courtId: number;
    requestId: number;
  } | null>(null);

  private readonly _resource = rxResource({
    params: () => this._request(),
    defaultValue: null as PasswordResetRequestResourceValue | null,
    stream: ({ params }) => {
      if (!params) return of(null);
      return this._http
        .forgotPassword({ email: params.email, court_id: params.courtId })
        .pipe(
          map(() => ({
            email: params.email,
            courtId: params.courtId,
            requestId: params.requestId,
          })),
        );
    },
  });

  public readonly value = this._resource.value;
  public readonly error = this._resource.error;
  public readonly isLoading = this._resource.isLoading;

  public readonly currentRequestId = computed(
    () => this._request()?.requestId ?? 0,
  );

  public requestReset(params: { email: string; courtId: number }) {
    this._requestId.update((id) => id + 1);
    const requestId = this._requestId();
    this._request.set({
      email: params.email,
      courtId: params.courtId,
      requestId,
    });
  }

  public reset(): void {
    this._request.set(null);
  }
}
